import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, Filter, X, Search, ChevronRight, MapPin, Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to strip HTML tags for plain text display
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const JobsPage = () => {
  const { t, language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRoleType, setSelectedRoleType] = useState('');
  const [selectedExperienceLevel, setSelectedExperienceLevel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API}/jobs`);
        setJobs(res.data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const locations = [...new Set(jobs.map(j => j.location))].filter(Boolean);
  const roleTypes = [...new Set(jobs.map(j => j.role_type))].filter(Boolean);
  const experienceLevels = [...new Set(jobs.map(j => j.experience_level))].filter(Boolean);
  const categories = [...new Set(jobs.map(j => j.category))].filter(Boolean);

  let filteredJobs = jobs.filter(job => {
    if (selectedLocation && selectedLocation !== 'all' && job.location !== selectedLocation) return false;
    if (selectedRoleType && selectedRoleType !== 'all' && job.role_type !== selectedRoleType) return false;
    if (selectedExperienceLevel && selectedExperienceLevel !== 'all' && job.experience_level !== selectedExperienceLevel) return false;
    if (selectedCategory && selectedCategory !== 'all' && job.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const title = language === 'sv' ? job.title_sv : job.title_en;
      const summary = stripHtml(language === 'sv' ? job.summary_sv : job.summary_en);
      if (!title?.toLowerCase().includes(query) && !summary?.toLowerCase().includes(query)) return false;
    }
    return true;
  });

  if (sortBy === 'newest') {
    filteredJobs = filteredJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } else if (sortBy === 'deadline') {
    filteredJobs = filteredJobs.sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    });
  }

  const clearFilters = () => {
    setSelectedLocation('');
    setSelectedRoleType('');
    setSelectedExperienceLevel('');
    setSelectedCategory('');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedLocation || selectedRoleType || selectedExperienceLevel || selectedCategory || searchQuery;

  const isNewJob = (createdAt) => {
    const jobDate = new Date(createdAt);
    const now = new Date();
    const diffDays = (now - jobDate) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  return (
    <div data-testid="jobs-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={language === 'sv' 
          ? 'Karriär | Lediga tjänster och konsultuppdrag' 
          : 'Careers | Jobs & Consulting Assignments'}
        description={language === 'sv'
          ? 'Sök lediga tjänster och konsultuppdrag hos Flowingo AB. Vi söker VVS-konsulter, projektledare och tekniker för spännande uppdrag i hela Sverige.'
          : 'Search for jobs and consulting assignments at Flowingo AB. We are looking for HVAC consultants, project managers and technicians for exciting assignments throughout Sweden.'}
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Karriär' : 'Careers', url: 'https://flowingo.se/jobb' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': 'https://flowingo.se/jobb',
            name: language === 'sv' ? 'Karriär - Lediga tjänster och konsultuppdrag' : 'Careers - Jobs and Consulting Assignments',
            description: 'Karriärmöjligheter inom VVS hos Flowingo AB',
            isPartOf: { '@id': 'https://flowingo.se/#website' },
          }
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{t('nav.jobs')}</span>
        </nav>
        <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
          {language === 'sv' ? 'Karriär hos Flowingo' : 'Careers at Flowingo'}
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
          {language === 'sv' 
            ? 'Lediga tjänster och konsultuppdrag' 
            : 'Jobs & Consulting Assignments'}
        </h1>
        <p className="text-stone-600 max-w-2xl">
          {language === 'sv'
            ? 'Vi erbjuder konsultuppdrag hos ledande aktörer i olika branscher. Flowingo söker ständigt efter duktiga ingenjörer som vill ta sig an spännande och utmanande uppdrag.'
            : 'We offer consulting assignments with leading companies in various industries. Flowingo is constantly looking for skilled engineers who want to take on exciting and challenging assignments.'}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className={`lg:w-72 shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white p-6 border border-stone-200 sticky top-28">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-stone-900 font-semibold flex items-center gap-2">
                  <Filter className="w-4 h-4" /> {t('jobs.filter')}
                </h3>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-stone-500 hover:text-stone-900 text-xs">
                    <X className="w-3 h-3 mr-1" /> {language === 'sv' ? 'Rensa' : 'Clear'}
                  </Button>
                )}
              </div>

              <div className="mb-6">
                <label className="text-stone-600 text-sm mb-2 block">{language === 'sv' ? 'Sök' : 'Search'}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'sv' ? 'Sök jobb...' : 'Search jobs...'}
                    className="pl-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 rounded-none"
                    data-testid="job-search"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="text-stone-600 text-sm mb-2 block">{t('jobs.location')}</label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger data-testid="filter-location" className="bg-white border-stone-300 text-stone-900 rounded-none">
                    <SelectValue placeholder={t('jobs.allLocations')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200">
                    <SelectItem value="all" className="text-stone-700">{t('jobs.allLocations')}</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc} value={loc} className="text-stone-700">{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <label className="text-stone-600 text-sm mb-2 block">{t('jobs.roleType')}</label>
                <Select value={selectedRoleType} onValueChange={setSelectedRoleType}>
                  <SelectTrigger data-testid="filter-role-type" className="bg-white border-stone-300 text-stone-900 rounded-none">
                    <SelectValue placeholder={t('jobs.allRoleTypes')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200">
                    <SelectItem value="all" className="text-stone-700">{t('jobs.allRoleTypes')}</SelectItem>
                    {roleTypes.map((type) => (
                      <SelectItem key={type} value={type} className="text-stone-700">{t(`roleTypes.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <label className="text-stone-600 text-sm mb-2 block">{t('jobs.experienceLevel')}</label>
                <Select value={selectedExperienceLevel} onValueChange={setSelectedExperienceLevel}>
                  <SelectTrigger data-testid="filter-experience" className="bg-white border-stone-300 text-stone-900 rounded-none">
                    <SelectValue placeholder={t('jobs.allExperienceLevels')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200">
                    <SelectItem value="all" className="text-stone-700">{t('jobs.allExperienceLevels')}</SelectItem>
                    {experienceLevels.map((level) => (
                      <SelectItem key={level} value={level} className="text-stone-700">{t(`experienceLevels.${level}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <label className="text-stone-600 text-sm mb-2 block">{t('jobs.jobCategory')}</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger data-testid="filter-job-category" className="bg-white border-stone-300 text-stone-900 rounded-none">
                    <SelectValue placeholder={t('jobs.allCategories')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200">
                    <SelectItem value="all" className="text-stone-700">{t('jobs.allCategories')}</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-stone-700">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </aside>

          <div className="flex-1">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="lg:hidden">
                <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-stone-300 text-stone-700 rounded-none">
                  <Filter className="w-4 h-4 mr-2" /> {t('jobs.filter')}
                </Button>
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <span className="text-stone-500 text-sm hidden sm:inline">{t('jobs.sortBy')}:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 bg-white border-stone-300 text-stone-900 rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200">
                    <SelectItem value="newest" className="text-stone-700">{t('jobs.newest')}</SelectItem>
                    <SelectItem value="deadline" className="text-stone-700">{t('jobs.deadline')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mb-6 text-stone-600 text-sm">
              {filteredJobs.length} {language === 'sv' ? 'lediga tjänster' : 'open positions'}
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="bg-white h-40 animate-pulse border border-stone-200" />
                ))}
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-600">{t('jobs.noJobs')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredJobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/jobb/${job.slug || job.id}`}
                    data-testid={`job-card-${job.id}`}
                    className="block bg-white p-6 border border-stone-200 hover:border-stone-300 hover:shadow-sm transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-stone-900">
                            {language === 'sv' ? job.title_sv : job.title_en}
                          </h3>
                          {isNewJob(job.created_at) && (
                            <span className="px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                              {t('jobs.newJob')}
                            </span>
                          )}
                          <span className="px-2 py-0.5 text-xs bg-stone-100 text-stone-600">
                            {t(`roleTypes.${job.role_type}`)}
                          </span>
                        </div>
                        <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                          {stripHtml(language === 'sv' ? job.summary_sv : job.summary_en)}
                        </p>
                        <div className="flex items-center gap-4 text-stone-500 text-sm flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {job.location}
                          </span>
                          <span>{t(`experienceLevels.${job.experience_level}`)}</span>
                          <span className="text-stone-300">•</span>
                          <span>{job.category}</span>
                          {job.deadline && (
                            <>
                              <span className="text-stone-300">•</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {t('jobs.deadline')}: {new Date(job.deadline).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US')}
                              </span>
                            </>
                          )}
                          {job.job_id && (
                            <>
                              <span className="text-stone-300">•</span>
                              <span className="font-mono text-xs bg-stone-100 px-2 py-0.5">
                                {language === 'sv' ? 'Jobb-ID:' : 'Job-ID:'} {job.job_id}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 shrink-0 self-start rounded-none">
                        {t('jobs.apply')} <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
